/**
 * Gantt chart → A3 landscape PDF (jsPDF vector rendering).
 * Shares logo loading / stamping utilities with activity and snag exports.
 */

import { jsPDF } from 'jspdf';
import { loadLogoDataUrlForPdf, stampCompanyLogoOnPages } from './pdfCompanyLogo';
import { hexToRgb } from './branding';

// ─── Page geometry (fixed) ────────────────────────────────────────────────────
const PAGE_W    = 1190;
const PAGE_H    = 841;
const MARGIN    = 36;
const SIDEBAR_W = 220;
const HDR_TOP_H = 26;
const HDR_BOT_H = 38;
const HEADER_H  = HDR_TOP_H + HDR_BOT_H;
const TITLE_H   = 72;
const LEGEND_H  = 28;
const FOOTER_H  = 20;
// Clearance at bottom of every page for footer separator + text
const PAGE_FOOT_CLEAR = MARGIN + FOOTER_H + 4;

const PHASE_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316'];

// ─── Colour helpers ───────────────────────────────────────────────────────────
function rgb(hex) { return hexToRgb(hex) || { r: 100, g: 116, b: 139 }; }
function draw(pdf, hex)  { const c = rgb(hex); pdf.setDrawColor(c.r, c.g, c.b); }

// ─── Date helpers ─────────────────────────────────────────────────────────────
function parseDate(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function addDays(date, n) {
  const d = new Date(date); d.setDate(d.getDate() + n); return d;
}
function diffDays(a, b) {
  const a0 = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const b0 = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((b0 - a0) / 86400000);
}
function todayMidnight() {
  const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

// ─── Build display items ──────────────────────────────────────────────────────
function buildDisplayItems(tasks, phases) {
  const norm = (phases || []).map((p, i) =>
    typeof p === 'string' ? { name: p, color: PHASE_COLORS[i % PHASE_COLORS.length] } : p
  );
  if (!norm.length) return tasks.map(task => ({ type: 'task', task }));
  const items = [], byPhase = {}, ungrouped = [];
  for (const task of tasks) {
    if (task.phase && norm.some(p => p.name === task.phase)) {
      (byPhase[task.phase] = byPhase[task.phase] || []).push(task);
    } else ungrouped.push(task);
  }
  const phaseRange = (taskList) => {
    let phaseStart = null, phaseEnd = null;
    for (const t of taskList) {
      if (!t.start) continue;
      if (!phaseStart || t.start < phaseStart) phaseStart = t.start;
      const end = (!t.isMilestone && t.end) ? t.end : t.start;
      if (!phaseEnd || end > phaseEnd) phaseEnd = end;
    }
    return { phaseStart, phaseEnd };
  };
  for (const { name, color } of norm) {
    const pt = byPhase[name] || [];
    items.push({ type: 'phase', phase: name, count: pt.length, color, ...phaseRange(pt) });
    pt.forEach(task => items.push({ type: 'task', task }));
  }
  if (ungrouped.length > 0) {
    items.push({ type: 'phase', phase: '__ungrouped__', count: ungrouped.length, color: '#94a3b8', ...phaseRange(ungrouped) });
    ungrouped.forEach(task => items.push({ type: 'task', task }));
  }
  return items;
}

// ─── Dependency normalisation ─────────────────────────────────────────────────
function normDep(dep) {
  if (typeof dep === 'string') return { id: dep, type: 'FS', lag: 0 };
  return { id: dep.id, type: dep.type || 'FS', lag: dep.lag || 0 };
}

// ─── Schedule data (CPM + float) ──────────────────────────────────────────────
function computeScheduleData(tasks) {
  const taskMap   = Object.fromEntries(tasks.map(t => [t.id, t]));
  const valid     = tasks.filter(t => t.start && t.end && !t.isMilestone);
  if (!valid.length) return { criticalIds: new Set(), floatDays: new Map() };
  const successors = Object.fromEntries(tasks.map(t => [t.id, []]));
  tasks.forEach(t => {
    (t.dependsOn || []).forEach(raw => {
      const { id, type, lag } = normDep(raw);
      if (successors[id]) successors[id].push({ succId: t.id, type, lag });
    });
  });
  const projectEnd = valid.reduce((mx, t) => { const e = parseDate(t.end); return e > mx ? e : mx; }, parseDate(valid[0].end));
  const floatDays  = new Map();
  valid.forEach(task => {
    let float = diffDays(parseDate(task.end), projectEnd);
    for (const { succId, type, lag } of (successors[task.id] || [])) {
      const succ = taskMap[succId];
      if (!succ?.start) continue;
      let slack;
      switch (type) {
        case 'SS': slack = diffDays(parseDate(task.start), parseDate(succ.start)) - lag; break;
        case 'FF': slack = succ.end ? diffDays(parseDate(task.end), parseDate(succ.end)) - lag : float; break;
        case 'SF': slack = succ.end ? diffDays(parseDate(task.start), parseDate(succ.end)) - lag : float; break;
        default:   slack = diffDays(parseDate(task.end), parseDate(succ.start)) - lag; break;
      }
      if (slack < float) float = slack;
    }
    floatDays.set(task.id, float);
  });
  const criticalIds = new Set([...floatDays.entries()].filter(([, f]) => f <= 0).map(([id]) => id));
  return { criticalIds, floatDays };
}

// ─── Text truncation ──────────────────────────────────────────────────────────
function truncate(pdf, text, maxW) {
  if (pdf.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 1 && pdf.getTextWidth(t + '…') > maxW) t = t.slice(0, -1);
  return t + '…';
}

// ─── Main export ──────────────────────────────────────────────────────────────
export async function buildGanttExportPdf({
  tasks = [],
  teams = [],
  phases = [],
  areas = [],
  projectName = 'Project',
  projectAddress = '',
  companyLogoUrl = null,
  brandColor = null,
  revisionLabel = null,
  options = {},
}) {
  const {
    showArrows       = false,
    showCriticalPath = true,
    showBaseline     = true,
    showStatusDots   = true,
    showProgress     = true,
    stripeMode       = 'navy',
    milestoneColor   = 'navy',
  } = options;

  const todayd       = todayMidnight();
  const displayItems = buildDisplayItems(tasks, phases);
  const { criticalIds: rawCritical, floatDays } = computeScheduleData(tasks);
  const criticalIds  = showCriticalPath ? rawCritical : new Set();
  const hasDeps      = floatDays.size > 0 && tasks.some(t => t.dependsOn?.length > 0);
  const logo         = companyLogoUrl ? await loadLogoDataUrlForPdf(companyLogoUrl) : null;

  const brandRgb  = (brandColor && hexToRgb(brandColor)) || null;
  const navyRgb   = { r: 15, g: 23, b: 42 };
  const stripeRgb = stripeMode === 'brand' && brandRgb ? brandRgb : navyRgb;
  const mRgb      = milestoneColor === 'brand' && brandRgb ? brandRgb : navyRgb;

  // ── Date range ───────────────────────────────────────────────────────────
  let viewStart, totalDays;
  const validTasks = tasks.filter(t => t.start && t.end);
  if (validTasks.length === 0) {
    viewStart = todayd; totalDays = 60;
  } else {
    let mn = null, mx = null;
    for (const t of validTasks) {
      const s = parseDate(t.start), e = parseDate(t.end);
      if (!mn || s < mn) mn = s;
      if (!mx || e > mx) mx = e;
    }
    viewStart = mn;
    totalDays = Math.max(14, diffDays(viewStart, addDays(mx, 7)));
  }

  // ── Timeline geometry ────────────────────────────────────────────────────
  // Expand sidebar when extra columns are present so they stay inside the sidebar boundary.
  const FLOAT_COL_W = hasDeps ? 28 : 0;
  const EFF_SIDEBAR_W = SIDEBAR_W + FLOAT_COL_W;
  const TIMELINE_X  = MARGIN + EFF_SIDEBAR_W;
  const TIMELINE_W  = PAGE_W - MARGIN - TIMELINE_X;
  const dayW        = Math.max(1.5, TIMELINE_W / totalDays);

  // ── Adaptive row height ──────────────────────────────────────────────────
  // Vertical anchors
  const CHART_TOP_P1   = MARGIN + TITLE_H + HEADER_H;   // 172
  const CHART_TOP_CONT = MARGIN + 30 + HEADER_H;         // 130

  // Available height on page 1 for task rows when targeting single-page fit.
  // Reserve LEGEND_H + 12 gap below content before the footer clearance.
  const p1FitAvail = PAGE_H - PAGE_FOOT_CLEAR - CHART_TOP_P1 - LEGEND_H - 12;
  const itemCount  = Math.max(1, displayItems.length);
  const idealRowH  = Math.floor(p1FitAvail / itemCount);

  // ≥20 px → scale to fit on one sheet (max 30); <20 → too dense, paginate at 20
  const ROW_H     = idealRowH >= 20 ? Math.min(30, idealRowH) : 20;
  const fontScale = Math.min(1.3, ROW_H / 22);

  // Derived sizes — font floors keep text legible at max compression
  const FS_TASK    = Math.max(5.5, 7.5 * fontScale);
  const FS_PHASE   = Math.max(5.0, 6.5 * fontScale);
  const DOT_R      = Math.max(1.8, 2.5 * fontScale);
  const DIAMOND_S  = Math.max(4,   Math.round(7 * fontScale));
  const BAR_TOP_PAD = Math.max(2,  Math.round(3 * fontScale));
  const BAR_H_PAD   = Math.max(4,  Math.round(6 * fontScale));

  // ── Pagination ───────────────────────────────────────────────────────────
  const ROWS_P1   = Math.max(1, Math.floor((PAGE_H - PAGE_FOOT_CLEAR - CHART_TOP_P1)   / ROW_H));
  const ROWS_CONT = Math.max(1, Math.floor((PAGE_H - PAGE_FOOT_CLEAR - CHART_TOP_CONT) / ROW_H));

  const pages = [];
  let rem = [...displayItems];
  pages.push(rem.splice(0, ROWS_P1));
  while (rem.length > 0) pages.push(rem.splice(0, ROWS_CONT));
  const totalPages = pages.length;

  // ── jsPDF instance ────────────────────────────────────────────────────────
  const pdf = new jsPDF({ unit: 'pt', format: 'a3', orientation: 'landscape' });

  const hasBaseline   = showBaseline && tasks.some(t => t.baseline);
  const hasMilestones = tasks.some(t => t.isMilestone);
  const chartW        = PAGE_W - 2 * MARGIN;
  const hasPhases     = phases && phases.length > 0;

  const DAY_SHORT   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const DAY_INITIAL = ['Su',  'M',   'Tu',  'W',   'Th',  'F',   'Sa'];

  // ── Draw each page ────────────────────────────────────────────────────────
  for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
    if (pageIdx > 0) pdf.addPage('a3', 'landscape');

    const isFirst    = pageIdx === 0;
    const isLastPage = pageIdx === totalPages - 1;
    const pageItems  = pages[pageIdx];
    const chartTopY  = isFirst ? CHART_TOP_P1 : CHART_TOP_CONT;
    const chartH     = pageItems.length * ROW_H;
    const chartBorderT = chartTopY - HEADER_H;
    const chartTotalH  = HEADER_H + chartH;

    // Page background
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, PAGE_W, PAGE_H, 'F');

    // Letterhead stripe
    if (stripeMode !== 'none') {
      pdf.setFillColor(stripeRgb.r, stripeRgb.g, stripeRgb.b);
      pdf.rect(0, 0, PAGE_W, 8, 'F');
    }

    // ── Title block ────────────────────────────────────────────────────────
    if (isFirst) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(20);
      pdf.setTextColor(15, 23, 42);
      pdf.text(projectName || 'Project', MARGIN, MARGIN + 20);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9.5);
      pdf.setTextColor(71, 85, 105);
      if (projectAddress) pdf.text(projectAddress, MARGIN, MARGIN + 35);

      pdf.setFontSize(8.5);
      pdf.setTextColor(148, 163, 184);
      const genDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      pdf.text(`Generated ${genDate}`, MARGIN, MARGIN + 50);
      if (revisionLabel) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8.5);
        pdf.setTextColor(15, 23, 42);
        pdf.text(revisionLabel, PAGE_W - MARGIN, MARGIN + 50, { align: 'right' });
      }

      // Key dates strip
      const milestoneTasks = tasks.filter(t => t.isMilestone && t.start).sort((a, b) => a.start < b.start ? -1 : 1);
      if (milestoneTasks.length > 0) {
        const strip = milestoneTasks.map(t => {
          const ds = parseDate(t.start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
          return `◆ ${ds} — ${t.name}`;
        }).join('   ');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(15, 23, 42);
        pdf.text('KEY DATES', PAGE_W - MARGIN, MARGIN + 42, { align: 'right' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(71, 85, 105);
        const maxW = PAGE_W - MARGIN * 2 - pdf.getTextWidth('Generated ' + genDate) - 20;
        pdf.text(truncate(pdf, strip, maxW), PAGE_W - MARGIN, MARGIN + 52, { align: 'right' });
      }

      // "PROJECT SCHEDULE" label
      pdf.setFillColor(15, 23, 42);
      pdf.rect(MARGIN, MARGIN + TITLE_H - 18, 3, 13, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(15, 23, 42);
      pdf.text('PROJECT SCHEDULE', MARGIN + 8, MARGIN + TITLE_H - 8);
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(15, 23, 42);
      pdf.text(projectName || 'Project', MARGIN, MARGIN + 18);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(148, 163, 184);
      pdf.text('continued', MARGIN + pdf.getTextWidth((projectName || 'Project') + ' '), MARGIN + 18);
    }

    // ── Chart border + divider ─────────────────────────────────────────────
    pdf.setDrawColor(215, 220, 226);
    pdf.setLineWidth(0.6);
    pdf.rect(MARGIN, chartBorderT, chartW, chartTotalH, 'S');

    pdf.setDrawColor(215, 220, 226);
    pdf.setLineWidth(0.4);
    pdf.line(TIMELINE_X, chartBorderT, TIMELINE_X, chartBorderT + chartTotalH);

    // ── Sidebar header ─────────────────────────────────────────────────────
    pdf.setFillColor(246, 248, 250);
    pdf.rect(MARGIN, chartBorderT, EFF_SIDEBAR_W, HEADER_H, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.5);
    pdf.setTextColor(148, 163, 184);
    pdf.text('TASK', MARGIN + 10, chartBorderT + HEADER_H - 9);
    pdf.text('DUR', MARGIN + SIDEBAR_W - 4, chartBorderT + HEADER_H - 9, { align: 'right' });
    if (hasDeps) pdf.text('FLOAT', MARGIN + EFF_SIDEBAR_W - 2, chartBorderT + HEADER_H - 9, { align: 'right' });

    // ── Timeline header: month row ─────────────────────────────────────────
    pdf.setFillColor(246, 248, 250);
    pdf.rect(TIMELINE_X, chartBorderT, TIMELINE_W, HDR_TOP_H, 'F');

    let mi = 0;
    while (mi < totalDays) {
      const d  = addDays(viewStart, mi);
      const mo = d.getMonth(), yr = d.getFullYear();
      let count = 0;
      while (mi + count < totalDays) {
        const nd = addDays(viewStart, mi + count);
        if (nd.getMonth() !== mo || nd.getFullYear() !== yr) break;
        count++;
      }
      const cellX = TIMELINE_X + mi * dayW;
      const cellW = count * dayW;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.setTextColor(71, 85, 105);
      const longLabel  = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      const shortLabel = d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      const label = pdf.getTextWidth(longLabel) < cellW - 6 ? longLabel
        : (pdf.getTextWidth(shortLabel) < cellW - 4 ? shortLabel : '');
      if (label) pdf.text(label, cellX + 5, chartBorderT + HDR_TOP_H - 8);
      pdf.setDrawColor(215, 220, 226);
      pdf.setLineWidth(0.3);
      pdf.line(cellX + cellW, chartBorderT, cellX + cellW, chartBorderT + HDR_TOP_H);
      mi += count;
    }

    // Divider between header rows
    pdf.setDrawColor(215, 220, 226);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, chartBorderT + HDR_TOP_H, MARGIN + chartW, chartBorderT + HDR_TOP_H);

    // ── Timeline header: day row ───────────────────────────────────────────
    pdf.setFillColor(250, 251, 252);
    pdf.rect(TIMELINE_X, chartBorderT + HDR_TOP_H, TIMELINE_W, HDR_BOT_H, 'F');

    for (let j = 0; j < totalDays; j++) {
      const d         = addDays(viewStart, j);
      const dow       = d.getDay();
      const isWeekend = dow === 0 || dow === 6;
      const isMonday  = dow === 1;
      const cellX     = TIMELINE_X + j * dayW;
      const cellY     = chartBorderT + HDR_TOP_H;

      if (isWeekend) {
        pdf.setFillColor(238, 241, 246);
        pdf.rect(cellX, cellY, dayW, HDR_BOT_H, 'F');
      }

      const textGrey = isWeekend ? [148, 163, 184] : [100, 116, 139];
      const numColor = isWeekend ? [148, 163, 184] : [15, 23, 42];

      if (dayW >= 20) {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(6); pdf.setTextColor(...textGrey);
        pdf.text(DAY_SHORT[dow], cellX + dayW / 2, cellY + 12, { align: 'center' });
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(...numColor);
        pdf.text(String(d.getDate()), cellX + dayW / 2, cellY + HDR_BOT_H - 7, { align: 'center' });
      } else if (dayW >= 12) {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(5.5); pdf.setTextColor(...textGrey);
        pdf.text(DAY_INITIAL[dow], cellX + dayW / 2, cellY + 11, { align: 'center' });
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5); pdf.setTextColor(...numColor);
        pdf.text(String(d.getDate()), cellX + dayW / 2, cellY + HDR_BOT_H - 7, { align: 'center' });
      } else if (dayW >= 7) {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(6); pdf.setTextColor(...textGrey);
        pdf.text(String(d.getDate()), cellX + dayW / 2, cellY + HDR_BOT_H / 2 + 2, { align: 'center' });
      } else if (isMonday) {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(5.5); pdf.setTextColor(100, 116, 139);
        const lbl = `Mon ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`;
        if (pdf.getTextWidth(lbl) < 7 * dayW - 2) pdf.text(lbl, cellX + 2, cellY + HDR_BOT_H / 2 + 2);
      }

      if (isMonday) {
        pdf.setDrawColor(200, 210, 220); pdf.setLineWidth(0.5);
        pdf.line(cellX, cellY, cellX, cellY + HDR_BOT_H);
      } else if (dayW >= 7 && j > 0) {
        pdf.setDrawColor(228, 232, 236); pdf.setLineWidth(0.2);
        pdf.line(cellX, cellY, cellX, cellY + HDR_BOT_H);
      }
    }

    // ── Row backgrounds ────────────────────────────────────────────────────
    pageItems.forEach((item, rowIdx) => {
      const rowY = chartTopY + rowIdx * ROW_H;
      if (item.type === 'phase') {
        const c = rgb(item.color);
        pdf.setFillColor(Math.round(c.r * 0.08 + 246 * 0.92), Math.round(c.g * 0.08 + 248 * 0.92), Math.round(c.b * 0.08 + 250 * 0.92));
        pdf.rect(MARGIN, rowY, chartW, ROW_H, 'F');
        pdf.setFillColor(c.r, c.g, c.b);
        pdf.rect(MARGIN, rowY, 3, ROW_H, 'F');
        pdf.rect(TIMELINE_X, rowY, 3, ROW_H, 'F');
      } else if (rowIdx % 2 === 1) {
        pdf.setFillColor(249, 250, 252);
        pdf.rect(MARGIN, rowY, chartW, ROW_H, 'F');
      }
      pdf.setDrawColor(228, 232, 236);
      pdf.setLineWidth(0.2);
      pdf.line(MARGIN, rowY + ROW_H, MARGIN + chartW, rowY + ROW_H);
    });

    // ── Weekend shading + week-boundary lines ──────────────────────────────
    for (let j = 0; j < totalDays; j++) {
      const d         = addDays(viewStart, j);
      const dow       = d.getDay();
      const isWeekend = dow === 0 || dow === 6;
      const colX      = TIMELINE_X + j * dayW;

      if (isWeekend && dayW >= 4) {
        pdf.setFillColor(244, 246, 250);
        pdf.setGState(new pdf.GState({ opacity: 0.55 }));
        pdf.rect(colX, chartTopY, dayW, chartH, 'F');
        pdf.setGState(new pdf.GState({ opacity: 1 }));
      }

      if (dow === 1 && j > 0) {
        pdf.setDrawColor(210, 216, 224); pdf.setLineWidth(0.35);
        pdf.line(colX, chartTopY, colX, chartTopY + chartH);
      }
    }

    // ── Sidebar + bars ─────────────────────────────────────────────────────
    pageItems.forEach((item, rowIdx) => {
      const rowY  = chartTopY + rowIdx * ROW_H;
      const midY  = rowY + ROW_H / 2;

      if (item.type === 'phase') {
        const c = rgb(item.color);
        // Sidebar label
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(FS_PHASE);
        pdf.setTextColor(c.r, c.g, c.b);
        const label = item.phase === '__ungrouped__' ? 'UNGROUPED' : item.phase.toUpperCase();
        pdf.text(truncate(pdf, label, EFF_SIDEBAR_W - 18), MARGIN + 10, midY + FS_PHASE * 0.35);
        // Rollup summary bar in timeline
        if (item.phaseStart && item.phaseEnd) {
          const barThick = Math.max(3, Math.round(4 * fontScale));
          const capW     = Math.max(3, Math.round(5 * fontScale));
          const capExt   = Math.max(5, Math.round(7 * fontScale));
          const barTopY  = rowY + Math.floor((ROW_H - barThick - capExt) / 2);
          const bx       = TIMELINE_X + diffDays(viewStart, parseDate(item.phaseStart)) * dayW;
          const bw       = Math.max(dayW, (diffDays(parseDate(item.phaseStart), parseDate(item.phaseEnd)) + 1) * dayW);
          const clipBx   = Math.max(TIMELINE_X, bx);
          const clipBw   = Math.min(bx + bw, PAGE_W - MARGIN) - clipBx;
          if (clipBw > 0) {
            pdf.setFillColor(c.r, c.g, c.b);
            pdf.rect(clipBx, barTopY, clipBw, barThick, 'F');
            if (bx >= TIMELINE_X)
              pdf.rect(bx, barTopY, capW, barThick + capExt, 'F');
            if (bx + bw <= PAGE_W - MARGIN)
              pdf.rect(bx + bw - capW, barTopY, capW, barThick + capExt, 'F');
          }
        }
        return;
      }

      const { task } = item;
      const team     = teams.find(t => t.name === task.team);
      const colorHex = team?.color || '#64748b';
      const c        = rgb(colorHex);

      // Sidebar two-line layout: task name on line 1, team · area subtitle on line 2
      const nameX    = MARGIN + (hasPhases ? 14 : 8);
      const durW     = 26;
      const floatW   = hasDeps ? 26 : 0;
      const nameMaxW = Math.max(36, SIDEBAR_W - (nameX - MARGIN) - durW - floatW - 4);
      const line1Y   = midY - ROW_H * 0.14;
      const line2Y   = midY + ROW_H * 0.26;
      const subFS    = Math.max(4.5, FS_TASK * 0.82);

      // Task name (line 1)
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(FS_TASK);
      pdf.setTextColor(30, 41, 59);
      pdf.text(truncate(pdf, task.name || '', nameMaxW), nameX, line1Y);

      // Duration (line 1, right-aligned)
      const fmtD = (iso) => {
        const d = parseDate(iso);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      };
      if (task.start && task.end) {
        const dur = diffDays(parseDate(task.start), parseDate(task.end)) + 1;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(Math.max(5.0, FS_TASK * 0.87));
        pdf.setTextColor(100, 116, 139);
        pdf.text(`${dur}d`, MARGIN + SIDEBAR_W - 4, line1Y, { align: 'right' });
      }

      // Date range (line 2, right-aligned) — computed first so subtitle knows its boundary
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(subFS);
      const dateRange = (task.start && task.end)
        ? `${fmtD(task.start)} – ${fmtD(task.end)}` : '';
      const dateRangeW = dateRange ? pdf.getTextWidth(dateRange) : 0;
      const subRightBound = MARGIN + SIDEBAR_W - 4 - (dateRangeW > 0 ? dateRangeW + 5 : 0);

      if (dateRange) {
        pdf.setTextColor(148, 163, 184);
        pdf.text(dateRange, MARGIN + SIDEBAR_W - 4, line2Y, { align: 'right' });
      }

      // Subtitle: team dot · team name · area (line 2, constrained to not overlap date)
      let subX = nameX;
      if (team) {
        pdf.setFillColor(c.r, c.g, c.b);
        pdf.circle(subX + DOT_R * 0.75, line2Y - DOT_R * 0.6, DOT_R * 0.85, 'F');
        subX += DOT_R * 2.2;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(subFS);
        pdf.setTextColor(100, 116, 139);
        const teamAvail = Math.max(0, subRightBound - subX);
        const teamLabel = truncate(pdf, team.name, teamAvail);
        pdf.text(teamLabel, subX, line2Y);
        subX += pdf.getTextWidth(teamLabel);
      }
      if (team && task.area && subX < subRightBound) {
        pdf.setFontSize(subFS);
        pdf.setTextColor(148, 163, 184);
        pdf.text(' · ', subX, line2Y);
        subX += pdf.getTextWidth(' · ');
      }
      if (task.area && subX < subRightBound) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(subFS);
        pdf.setTextColor(100, 116, 139);
        const areaAvail = Math.max(0, subRightBound - subX);
        pdf.text(truncate(pdf, task.area, areaAvail), subX, line2Y);
      }

      // Float — within EFF_SIDEBAR_W
      if (hasDeps) {
        const f = floatDays.get(task.id);
        if (f != null) {
          const isCrit = f <= 0;
          const isNear = f > 0 && f <= 5;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(Math.max(5.0, FS_TASK * 0.87));
          if (isCrit)      pdf.setTextColor(239, 68, 68);
          else if (isNear) pdf.setTextColor(245, 158, 11);
          else             pdf.setTextColor(148, 163, 184);
          pdf.text(isCrit ? '—' : `${f}d`, MARGIN + EFF_SIDEBAR_W - 2, line1Y, { align: 'right' });
        }
      }

      if (!task.start || !task.end) return;

      const s      = parseDate(task.start);
      const e      = parseDate(task.end);
      const barTop = rowY + BAR_TOP_PAD;
      const barH   = ROW_H - BAR_H_PAD;

      // Milestone diamond
      if (task.isMilestone) {
        const cx = TIMELINE_X + diffDays(viewStart, s) * dayW + dayW / 2;
        const cy = midY;
        pdf.setFillColor(mRgb.r, mRgb.g, mRgb.b);
        pdf.triangle(cx, cy - DIAMOND_S, cx + DIAMOND_S, cy, cx, cy + DIAMOND_S, 'F');
        pdf.triangle(cx, cy - DIAMOND_S, cx - DIAMOND_S, cy, cx, cy + DIAMOND_S, 'F');
        const dateStr = s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(Math.max(5, FS_TASK * 0.8));
        pdf.setTextColor(mRgb.r, mRgb.g, mRgb.b);
        if (cx + DIAMOND_S + 2 + pdf.getTextWidth(dateStr) < PAGE_W - MARGIN)
          pdf.text(dateStr, cx + DIAMOND_S + 2, cy + 2.5);
        return;
      }

      // Bar
      const barX  = TIMELINE_X + diffDays(viewStart, s) * dayW;
      const barW  = Math.max(dayW, (diffDays(s, e) + 1) * dayW);
      const clipX = Math.max(TIMELINE_X, barX);
      const clipW = Math.min(barX + barW, PAGE_W - MARGIN) - clipX;
      if (clipW <= 0) return;

      // Baseline ghost
      if (showBaseline && task.baseline) {
        const bs  = parseDate(task.baseline.start);
        const be  = parseDate(task.baseline.end);
        const bX  = TIMELINE_X + diffDays(viewStart, bs) * dayW;
        const bW  = Math.max(dayW, (diffDays(bs, be) + 1) * dayW);
        const bcX = Math.max(TIMELINE_X, bX);
        const bcW = Math.min(bX + bW, PAGE_W - MARGIN) - bcX;
        if (bcW > 0) {
          draw(pdf, colorHex);
          pdf.setLineWidth(0.75);
          pdf.setLineDashPattern([3, 2], 0);
          pdf.roundedRect(bcX, barTop + 1, bcW, barH - 2, 2, 2, 'S');
          pdf.setLineDashPattern([], 0);
        }
      }

      // Main bar
      pdf.setFillColor(c.r, c.g, c.b);
      pdf.roundedRect(clipX, barTop, clipW, barH, 2, 2, 'F');

      // Gloss highlight
      pdf.setFillColor(255, 255, 255);
      pdf.saveGraphicsState();
      pdf.setGState(new pdf.GState({ opacity: 0.12 }));
      pdf.rect(clipX, barTop, clipW, barH * 0.45, 'F');
      pdf.restoreGraphicsState();

      // % Complete fill overlay
      if (showProgress) {
        const pct = task.status === 'complete' ? 100 : (task.percentComplete ?? 0);
        if (pct > 0) {
          const dr = Math.max(0, c.r - 45), dg = Math.max(0, c.g - 45), db = Math.max(0, c.b - 45);
          pdf.setFillColor(dr, dg, db);
          const fillW = clipW * Math.min(100, pct) / 100;
          pdf.roundedRect(clipX, barTop, Math.min(fillW, clipW), barH, 2, 2, 'F');
        }
      }

      // Critical / near-critical ring
      if (showCriticalPath && criticalIds.has(task.id)) {
        pdf.setDrawColor(239, 68, 68);
        pdf.setLineWidth(1.1);
        pdf.roundedRect(clipX - 0.75, barTop - 0.75, clipW + 1.5, barH + 1.5, 2.5, 2.5, 'S');
      } else if (hasDeps) {
        const f = floatDays.get(task.id);
        if (f != null && f > 0 && f <= 5) {
          pdf.setDrawColor(245, 158, 11);
          pdf.setLineWidth(1.0);
          pdf.roundedRect(clipX - 0.75, barTop - 0.75, clipW + 1.5, barH + 1.5, 2.5, 2.5, 'S');
        }
      }

      // Status/delay dot
      if (showStatusDots) {
        const taskStatus = task.status || 'not_started';
        let dotRgb = null;
        if (taskStatus !== 'complete') {
          if (todayd > e)                                       dotRgb = [239, 68, 68];
          else if (taskStatus === 'not_started' && todayd >= s) dotRgb = [245, 158, 11];
        }
        if (dotRgb) {
          const dotX = Math.min(clipX + clipW - 5, PAGE_W - MARGIN - 5);
          pdf.setFillColor(dotRgb[0], dotRgb[1], dotRgb[2]);
          pdf.circle(dotX, barTop + barH / 2, DOT_R, 'F');
        }
      }
    });

    // ── Dependency arrows ──────────────────────────────────────────────────
    if (showArrows) {
      const taskPos   = {};
      const arrowSize = Math.max(3, Math.round(5 * fontScale));
      pageItems.forEach((item, rowIdx) => {
        if (item.type === 'task') taskPos[item.task.id] = { rowIdx, task: item.task };
      });
      pageItems.forEach((item, idx) => {
        if (item.type !== 'task' || !item.task.dependsOn?.length) return;
        item.task.dependsOn.forEach(raw => {
          const { id: depId, type: depType, lag } = normDep(raw);
          const pred = taskPos[depId];
          if (!pred || !pred.task.start || !item.task.start) return;
          const predTask = pred.task;
          let x1, y1, x2, y2;
          const predY = chartTopY + pred.rowIdx * ROW_H + ROW_H / 2;
          const succY = chartTopY + idx * ROW_H + ROW_H / 2;
          switch (depType) {
            case 'SS':
              x1 = TIMELINE_X + diffDays(viewStart, parseDate(predTask.start)) * dayW;
              x2 = TIMELINE_X + (diffDays(viewStart, parseDate(item.task.start)) + lag) * dayW;
              break;
            case 'FF':
              if (!predTask.end || !item.task.end) return;
              x1 = TIMELINE_X + (diffDays(viewStart, parseDate(predTask.end)) + 1) * dayW;
              x2 = TIMELINE_X + (diffDays(viewStart, parseDate(item.task.end)) + 1 + lag) * dayW;
              break;
            case 'SF':
              x1 = TIMELINE_X + diffDays(viewStart, parseDate(predTask.start)) * dayW;
              x2 = item.task.end ? TIMELINE_X + (diffDays(viewStart, parseDate(item.task.end)) + 1 + lag) * dayW : x1;
              break;
            default:
              if (!predTask.end) return;
              x1 = TIMELINE_X + (diffDays(viewStart, parseDate(predTask.end)) + 1) * dayW;
              x2 = TIMELINE_X + (diffDays(viewStart, parseDate(item.task.start)) + lag) * dayW;
          }
          y1 = predY; y2 = succY;
          const isCrit = criticalIds.has(depId) && criticalIds.has(item.task.id);
          const cp   = Math.max(20, Math.abs(x2 - x1) * 0.4);
          if (isCrit) { pdf.setDrawColor(239, 68, 68);    pdf.setLineWidth(0.9); }
          else         { pdf.setDrawColor(148, 163, 184); pdf.setLineWidth(0.7); pdf.setLineDashPattern([3, 2], 0); }
          pdf.moveTo(x1, y1);
          pdf.curveTo(x1 + cp, y1, x2 - cp, y2, x2, y2);
          pdf.stroke();
          pdf.setLineDashPattern([], 0);
          const arrowC = isCrit ? [239, 68, 68] : [148, 163, 184];
          pdf.setFillColor(arrowC[0], arrowC[1], arrowC[2]);
          pdf.triangle(x2, y2, x2 - arrowSize, y2 - arrowSize / 2, x2 - arrowSize, y2 + arrowSize / 2, 'F');
        });
      });
    }

    // ── Legend — last page only, anchored below last row ───────────────────
    if (isLastPage) {
      const legendY = chartTopY + chartH + 12;
      let lx = MARGIN;

      const legendItem = (dotFn, label) => {
        dotFn();
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(71, 85, 105);
        pdf.text(label, lx + 2, legendY + 7);
        lx += pdf.getTextWidth(label) + 14;
      };

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(148, 163, 184);
      pdf.text('TEAMS', lx, legendY + 7);
      lx += pdf.getTextWidth('TEAMS') + 8;

      teams.forEach(team => {
        const c = rgb(team.color);
        legendItem(() => { pdf.setFillColor(c.r, c.g, c.b); pdf.circle(lx + 4, legendY + 4, 4, 'F'); lx += 12; }, team.name);
      });

      if (showStatusDots) {
        lx += 6;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(148, 163, 184);
        pdf.text('STATUS', lx, legendY + 7);
        lx += pdf.getTextWidth('STATUS') + 8;

        [
          { r: 148, g: 163, b: 184, l: 'Not started' },
          { r: 245, g: 158, b: 11,  l: 'In progress' },
          { r: 16,  g: 185, b: 129, l: 'Complete' },
        ].forEach(({ r: lr, g, b, l }) => {
          legendItem(() => { pdf.setFillColor(lr, g, b); pdf.circle(lx + 4, legendY + 4, 4, 'F'); lx += 12; }, l);
        });
      }

      if (criticalIds.size > 0) {
        lx += 6;
        legendItem(() => { pdf.setFillColor(239, 68, 68); pdf.circle(lx + 4, legendY + 4, 4, 'F'); lx += 12; }, 'Critical path');
      }

      if (hasDeps && [...floatDays.values()].some(f => f > 0 && f <= 5)) {
        lx += 6;
        legendItem(() => { pdf.setFillColor(245, 158, 11); pdf.circle(lx + 4, legendY + 4, 4, 'F'); lx += 12; }, 'Near critical (≤5d float)');
      }

      if (hasBaseline) {
        lx += 6;
        legendItem(() => {
          pdf.setDrawColor(148, 163, 184); pdf.setLineWidth(0.75); pdf.setLineDashPattern([3, 2], 0);
          pdf.rect(lx, legendY, 14, 8, 'S'); pdf.setLineDashPattern([], 0); lx += 18;
        }, 'Baseline');
      }

      if (hasMilestones) {
        lx += 6;
        legendItem(() => {
          const ds = 5, cx = lx + 6, cy = legendY + 4;
          pdf.setFillColor(mRgb.r, mRgb.g, mRgb.b);
          pdf.triangle(cx, cy - ds, cx + ds, cy, cx, cy + ds, 'F');
          pdf.triangle(cx, cy - ds, cx - ds, cy, cx, cy + ds, 'F');
          lx += 16;
        }, 'Milestone');
      }
    }

    // ── Footer ─────────────────────────────────────────────────────────────
    const footY = PAGE_H - MARGIN + 10;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(148, 163, 184);
    pdf.text(`Page ${pageIdx + 1} of ${totalPages}`, MARGIN, footY);
    pdf.text('Generated by CrewChain', PAGE_W - MARGIN, footY, { align: 'right' });
    pdf.setDrawColor(228, 232, 236);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, footY - 8, PAGE_W - MARGIN, footY - 8);
  }

  // ── Logo stamp ────────────────────────────────────────────────────────────
  if (logo) stampCompanyLogoOnPages(pdf, logo, { pages: 'first' });

  // ── Save ──────────────────────────────────────────────────────────────────
  const safe    = (projectName || 'project').replace(/[^a-z0-9]/gi, '-').toLowerCase();
  const revMatch = revisionLabel?.match(/Rev ([A-Z]) \(Version (\d+)\)/);
  const safeRev  = revMatch ? `_Rev${revMatch[1]}_v${revMatch[2]}` : '';
  const d        = new Date();
  const stamp    = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  pdf.save(`schedule-${safe}${safeRev}-${stamp}.pdf`);
}
